/* eslint-disable react/prop-types */
import {
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Spin,
} from 'antd';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import Table from 'components/Table';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';

import {
  deleteHistoricoDeSindicato,
  getHistoricosDeSindicato,
  patchHistoricoDeSindicato,
  postHistoricoDeSindicato,
} from 'api/catalogos/historicoDeSindicatos';
import { getPeriodosFiscales } from 'api/configuraciones/periodosFiscales';
import { getPeriodosDeNominasDetalles } from 'api/nominas/periodos/periodosDeNominasDetalle';
import { getPeriodosDeNominas } from 'api/nominas/periodos/periodosDeNominas';
import { getConfiguracionDescuentos } from 'api/nominas/descuentos/configuracionDescuentos';
import { getDatoOperacionalesDeEmpleados } from 'api/estructuraOrganizacional/datosOperacionalesDeEmpleados';
import { checkToken } from 'api/utils';

export const historicoDeSindicatoPermission = {
  permissionModel: 'historicodesindicato',
  catalogsModels: [],
  tabsModels: [],
};

const Sindicato = ({
  empleadoId,
  sindicatos,
  rules,
  formOperacionales,
  permission,
  isBlocked,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [periodosFiscales, setPeriodosFiscales] = useState([]);
  const [periodosDeNominasDetalles, setPeriodosDeNominasDetalles] = useState([]);
  const [periodosDeNominas, setPeriodosDeNominas] = useState([]);
  const [descuentos, setDescuentos] = useState([]);
  const [tipoDeNominaEmpleadoActual, setTipoDeNominaEmpleadoActual] = useState(null);

  const formInstances = [
    form,
  ];

  const fechaFinal = Form.useWatch('fecha_final', form);
  const sindicatoSeleccionado = Form.useWatch('sindicato', form);
  const periodoSeleccionado = Form.useWatch('periodo', form);

  const descuentoDelSindicato = descuentos.find(
    (d) => d.id === sindicatos.find((s) => s.id === sindicatoSeleccionado)?.descuento,
  );
  const requierePeriodoDeDescuento = !!descuentoDelSindicato;

  // Filtra los periodos de nómina detalle por el Periodo (fiscal) seleccionado
  // y por el Tipo de Nómina Empleado del dato operacional del empleado (plaza).
  const periodosDeNominasDetallesFiltrados = periodosDeNominasDetalles.filter((detalle) => {
    const periodoDeNomina = periodosDeNominas.find((pdn) => pdn.id === detalle.periodo_de_nomina);
    if (!periodoDeNomina) return false;
    if (periodoSeleccionado && periodoDeNomina.periodo_fiscal !== periodoSeleccionado) {
      return false;
    }
    if (tipoDeNominaEmpleadoActual
      && periodoDeNomina.tipo_de_nomina_empleado !== tipoDeNominaEmpleadoActual) {
      return false;
    }
    return true;
  });

  const normalizeResponse = (response) => {
    const list = response?.results || response || [];
    // Prioriza los registros con estatus Activo antes que los Inactivos.
    return formatReceived(list)
      .sort((a, b) => (b.estatus ? 1 : 0) - (a.estatus ? 1 : 0));
  };

  const sincronizarAtributos = (list) => {
    // Refleja la afiliación vigente en los campos Sindicalizado/Sindicato
    // de la pestaña Atributos (el backend actualiza el dato operacional).
    if (!formOperacionales) return;
    const vigente = list.find((e) => e.estatus);
    formOperacionales.setFieldsValue({
      sindicalizado: !!vigente,
      sindicato: vigente?.sindicato?.id || null,
    });
  };

  const fetchData = async () => {
    if (!empleadoId) return [];
    setLoading(true);
    const response = await getHistoricosDeSindicato({ empleado: empleadoId });
    const list = normalizeResponse(response);
    setData(list);
    setLoading(false);
    return list;
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      if (!empleadoId) {
        setData([]);
        return;
      }
      setLoading(true);
      await checkToken();
      const response = await getHistoricosDeSindicato({ empleado: empleadoId });
      if (mounted) {
        setData(normalizeResponse(response));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, [empleadoId]);

  useEffect(() => {
    let mounted = true;
    const fetchTipoDeNominaEmpleado = async () => {
      if (!empleadoId) {
        setTipoDeNominaEmpleadoActual(null);
        return;
      }
      const datosOperacionales = await getDatoOperacionalesDeEmpleados({ empleado: empleadoId });
      if (mounted) {
        setTipoDeNominaEmpleadoActual(datosOperacionales?.[0]?.tipo_de_nomina_empleado || null);
      }
    };
    fetchTipoDeNominaEmpleado();
    return () => { mounted = false; };
  }, [empleadoId]);

  useEffect(() => {
    let mounted = true;
    const fetchCatalogos = async () => {
      // silent403: este catálogo solo alimenta el formulario de Agregar/Editar afiliación
      // (botones que ya están ocultos/deshabilitados sin el permiso correspondiente) -- si
      // el usuario no tiene permiso para alguno, no tiene sentido avisarle de un dato que
      // nunca pidió ver.
      const [
        _periodosFiscales,
        _periodosDeNominasDetalles,
        _periodosDeNominas,
        _descuentos,
      ] = await Promise.all([
        getPeriodosFiscales(),
        getPeriodosDeNominasDetalles(undefined, { silent403: true }),
        getPeriodosDeNominas(undefined, { silent403: true }),
        getConfiguracionDescuentos(undefined, { silent403: true }),
      ]);
      if (mounted) {
        setPeriodosFiscales(_periodosFiscales);
        setPeriodosDeNominasDetalles(_periodosDeNominasDetalles);
        setPeriodosDeNominas(_periodosDeNominas);
        setDescuentos(_descuentos);
      }
    };
    fetchCatalogos();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
  };

  const onClickAdd = () => {
    setSelectedRowKeys([]);
    form.resetFields();
    setVisible(true);
  };

  const onClickEdit = () => {
    const [key] = selectedRowKeys;
    const match = data.find((item) => item.id === key);
    if (match) {
      form.setFieldsValue({
        ...match,
        sindicato: match.sindicato?.id,
        fecha_inicial: match.fecha_inicial ? moment(match.fecha_inicial) : null,
        fecha_final: match.fecha_final ? moment(match.fecha_final) : null,
      });
      setVisible(true);
    }
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
        empleado: empleadoId,
      };
      if (values.fecha_inicial?.format) {
        values.fecha_inicial = values.fecha_inicial.format('YYYY-MM-DD');
      }
      if (values.fecha_final?.format) {
        values.fecha_final = values.fecha_final.format('YYYY-MM-DD');
      }
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchHistoricoDeSindicato(key, values) : postHistoricoDeSindicato(values));
      if (saved) {
        onCancel();
        const list = await fetchData();
        sincronizarAtributos(list);
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteHistoricoDeSindicato(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          const list = await fetchData();
          sincronizarAtributos(list);
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Sindicato',
      dataIndex: 'sindicato',
      key: 'sindicato',
      width: 300,
      render: (val) => (val?.clave ? `${val.clave} ${val.descripcion}` : val?.descripcion),
    },
    {
      titleText: 'Fecha Inicial',
      dataIndex: 'fecha_inicial',
      key: 'fecha_inicial',
      width: 150,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
    },
    {
      titleText: 'Fecha Final',
      dataIndex: 'fecha_final',
      key: 'fecha_final',
      width: 150,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
    },
    {
      titleText: 'Solicitud',
      dataIndex: 'solicitud',
      key: 'solicitud',
      width: 250,
    },
    {
      titleText: 'Descuento',
      dataIndex: 'sindicato',
      key: 'sindicato_descuento',
      width: 300,
      render: (val) => descuentos.find((d) => d.id === val?.descuento)?.clave_y_concepto || '',
    },
    {
      titleText: 'Periodo',
      dataIndex: 'periodo',
      key: 'periodo',
      width: 120,
      render: (val) => periodosFiscales.find((e) => e.id === val)?.periodo || '',
    },
    {
      titleText: 'Periodo de Nómina Inicial del Descuento',
      dataIndex: 'periodo_de_nomina_detalle',
      key: 'periodo_de_nomina_detalle',
      width: 250,
      render: (val) => {
        const _periodo = periodosDeNominasDetalles.find((e) => e.id === val);
        return _periodo ? `${_periodo.numero_de_nomina} (${_periodo.fecha_inicial} al ${_periodo.fecha_final})` : '';
      },
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estatus',
      key: 'estatus',
      width: 120,
      render: (val) => (val ? 'Activo' : 'Inactivo'),
    },
  ];

  const selectedData = data.find((e) => e.id === selectedRowKeys[0]);

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Selección de Sindicato`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
          width="35%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="historico_de_sindicato"
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  name="sindicato"
                  label="Sindicato"
                  rules={rules.required}
                  hasFeedback
                >
                  <Select
                    labelProp="descripcion"
                    keyProp="id"
                    dataSource={sindicatos}
                    disabled={isBlocked}
                  />
                </Form.Item>
                <Form.Item
                  label="Descuento"
                >
                  <Input
                    disabled
                    value={descuentoDelSindicato?.clave_y_concepto || ''}
                  />
                </Form.Item>
                <Form.Item
                  name="fecha_inicial"
                  label="Fecha Inicial"
                  rules={rules.required}
                  hasFeedback
                >
                  <DatePicker
                    format="DD/MM/YYYY"
                    style={{ width: '100%' }}
                    disabled={isBlocked}
                  />
                </Form.Item>
                <Form.Item
                  name="fecha_final"
                  label="Fecha Final"
                  hasFeedback
                >
                  <DatePicker
                    format="DD/MM/YYYY"
                    style={{ width: '100%' }}
                    disabled={isBlocked}
                  />
                </Form.Item>
                <Form.Item
                  name="solicitud"
                  label="Solicitud"
                  rules={rules.required}
                  hasFeedback
                >
                  <Input
                    allowClear
                    maxLength={150}
                    disabled={isBlocked}
                  />
                </Form.Item>
                <Form.Item
                  name="periodo"
                  label="Periodo"
                  rules={requierePeriodoDeDescuento ? rules.required : []}
                  hasFeedback
                >
                  <Select
                    dataSource={periodosFiscales}
                    allowClear
                    disabled={isBlocked}
                    onChange={() => form.setFieldValue('periodo_de_nomina_detalle', undefined)}
                  />
                </Form.Item>
                <Form.Item
                  name="periodo_de_nomina_detalle"
                  label="Periodo de Nómina Inicial del Descuento"
                  rules={requierePeriodoDeDescuento ? rules.required : []}
                  hasFeedback
                >
                  <Select
                    keyProp="id"
                    dataSource={periodosDeNominasDetallesFiltrados}
                    render={(p) => `${p?.numero_de_nomina} (${p?.fecha_inicial} al ${p?.fecha_final})`}
                    allowClear
                    disabled={isBlocked}
                  />
                </Form.Item>
                <Form.Item
                  label="Estatus"
                >
                  <Input
                    disabled
                    value={fechaFinal ? 'Inactivo' : 'Activo'}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={selectedData?.sindicato?.descripcion || ''}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default Sindicato;
